<template>
  <div
    v-if="isOlympiad && material.olympiad"
    class="olympiad__image"
  >
    <img
      :src="olympiadImage"
      :alt="material.title"
      loading="lazy"
      @error="imageLoadError(index)"
    >
    <div>
      <p v-if="olympiadPeriod">
        {{ olympiadPeriod }}
      </p>
      <p v-if="material.olympiad.tAgeGroup">
        {{ material.olympiad.tAgeGroup }}
      </p>
    </div>
  </div>
  <div
    v-else
    class="material__images"
    @mousedown="handleSwipeStart"
    @touchstart="handleSwipeStart"
    @mouseup="handleSwipeEnd"
    @touchend="handleSwipeEnd"
  >
    <div
      class="material__images_slider"
      :style="{ transform: `translateX(-${activeImageIndex * 100}%)` }"
    >
      <img
        v-for="(image, index) in materialImages"
        :key="index"
        :src="image"
        :alt="image.filename"
        loading="lazy"
        @error="imageLoadError(index)"
        @dragstart.prevent
      >
    </div>

    <div
      v-if="materialImages.length > 1"
      class="material__images_points"
    >
      <span
        v-for="(image, index) in materialImages"
        :key="index"
        :class="{ active: index === activeImageIndex }"
        @click="activeImageIndex = index"
      />
    </div>
  </div>
</template>

<script>
import { getCategoryImageUrl, months } from '../constants';

export default {
  name: 'MaterialImages',
  props: {
    material: { type: Object, required: true },
  },
  data: () => ({
    activeImageIndex: 0,
    isMouseDown: false,
    startX: null,
  }),
  computed: {
    isOlympiad() {
      return Boolean(this.material.olympiadPack?.id);
    },
    olympiadImage() {
      if (this.material.covers.length) {
        return this.material.covers[0].url;
      }

      const olympiadCategory = this.material.categories?.[0] || { slug: 'olympiad', name: 'олимпиада' };
      return [getCategoryImageUrl(olympiadCategory)];
    },
    olympiadPeriod() {
      if (!this.material.olympiadPack?.id) return '';

      const startsAt = new Date(this.material.olympiadPack.startsAt);
      const endsAt = new Date(this.material.olympiadPack.endsAt);

      const startDay = startsAt.getDate();
      const startMonth = months[startsAt.getMonth()];
      const endDay = endsAt.getDate();
      const endMonth = months[endsAt.getMonth()];

      if (startMonth === endMonth) {
        return `${startDay} - ${endDay} ${startMonth}`;
      }

      return `${startDay} ${startMonth} - ${endDay} ${startMonth}`;
    },

    materialImages() {
      if (this.material.covers?.length) {
        return this.material.covers.map(({ url }) => url);
      }

      const materialCategory = this.material.categories?.[0] || { slug: 'articles', name: 'Статьи' };
      return [getCategoryImageUrl(materialCategory)];
    },
  },
  methods: {
    imageLoadError(index) {
      if (this.material.covers[index]) {
        // eslint-disable-next-line vue/no-mutating-props
        this.material.covers[index].url = getCategoryImageUrl(this.materialCategory);
      }
    },
    handleSwipeStart(event) {
      if (event.clientX) {
        this.isMouseDown = true;
        this.startX = event.clientX;
      } else {
        this.startX = event.changedTouches[0].clientX;
      }
    },
    handleSwipeEnd(event) {
      let endX = 0;
      if (event.clientX) {
        if (!this.isMouseDown) return;
        endX = event.clientX;
      } else {
        endX = event.changedTouches[0].clientX;
      }

      const swipeDistance = endX - this.startX;

      if (swipeDistance < -70 && this.activeImageIndex < this.materialImages.length - 1) {
        this.activeImageIndex += 1;
      }
      if (swipeDistance > 70 && this.activeImageIndex > 0) {
        this.activeImageIndex -= 1;
      }

      this.isMouseDown = false;
    },
  },
};
</script>

<style scoped lang="scss">

.material__images {
  width: 100%;
  position: relative;
  overflow-x: hidden;

  @include media-down($size-tablet) {
    width: calc(var(--scale) * 364);
    padding-bottom: 0;
  }

  @include media-down($size-mobile) {
    width: 100%;
  }

  &_slider {
    display: flex;
    transition-duration: 300ms;
    transition-timing-function: ease;

    img {
      flex-shrink: 0;
      aspect-ratio: 1/1;
      width: 100%;
      object-fit: cover;
      border-radius: calc(var(--scale) * 20);
      border: 1px solid #ECEDF0;

      @include media-down($size-tablet) {
        border-radius: calc(var(--scale) * 20);
      }
    }
  }

  &_points {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: calc(var(--scale) * 2) ;
    position: absolute;
    bottom: calc(var(--scale) * 6);
    left: 50%;
    transform: translateX(-50%);

    @include media-down($size-tablet) {
      position: relative;
      bottom: 0;
      margin-top: calc(var(--scale) * 6);
    }

    span {
      width: calc(var(--scale) * 8);
      height: calc(var(--scale) * 8);
      border-radius: 50%;
      background-color: #ECEDF0;
      transition: 200ms;

      &.active {
        background-color: #5237E6;
      }
    }
  }
}

.olympiad__image {
  position: relative;
  aspect-ratio: 1/1;
  width: calc(var(--scale) * 401);
  overflow: hidden;

  border: 1px solid #ECEDF0;
  border-radius: calc(var(--scale) * 20);
  padding: calc(var(--scale) * 20);

  @include media-down($size-tablet) {
    border-radius: calc(var(--scale) * 24);
  }

  img {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }

  div {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: calc(var(--scale) * 2);
    margin-bottom: calc(var(--scale) * 7);

    @include media-down($size-tablet) {
      margin-bottom: calc(var(--scale) * 15);
    }

    p {
      padding: calc(var(--scale) * 6) calc(var(--scale) * 11) calc(var(--scale) * 8);
      background-color: rgba(0, 0, 0, 0.01);
      border: 1px solid rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(10px);
      border-radius: calc(var(--scale) * 36);
      text-transform: lowercase;

      font-weight: 500;
      font-size: calc(var(--scale) * 14);
      line-height: 130%;
      color: #000000;
    }
  }
}

</style>
